import { APPLICATION_PAGES } from '../catalog/pages';
import { APPLICATION_REDIRECTS } from '../catalog/redirects';
import { matchesRoute, normalizeRoute } from './route';
import { IKnowledgeResult } from './knowledge-result.interface';
import { RESEARCH_ACCESS_GUIDANCE } from '../guides/research-access';


export function getApplicationKnowledge(route: string, question: string, history: { role: string; text: string }[] = []): IKnowledgeResult {
	let normalized = normalizeRoute(route);
	for (let attempt = 0; attempt < 8; attempt++) {
		if (APPLICATION_PAGES.some((page) => matchesRoute(page.route, normalized))) break;
		const redirect = APPLICATION_REDIRECTS.find((item) => item.route === normalized);
		if (!redirect || redirect.target === normalized) break;
		normalized = redirect.target;
	}
	const current = APPLICATION_PAGES.find((page) => page.route === normalized)
		|| APPLICATION_PAGES.find((page) => matchesRoute(page.route, normalized));
	const tokens = (value: string): string[] => value.toLowerCase().match(/[a-z0-9]+/g) || [];
	const stopWords = new Set(['the', 'and', 'for', 'how', 'what', 'this', 'that', 'can', 'with', 'from', 'are', 'does', 'have', 'page', 'explain', 'about']);
	const terms = new Set(tokens(question).filter((term) => term.length > 2 && !stopWords.has(term)));
	const previous = history.filter((item) => item.role === 'user').slice(-2).map((item) => item.text).join(' ').toLowerCase();
	const ranked = APPLICATION_PAGES.filter((page) => page !== current).map((page) => {
		const titleWords = tokens(`${page.title} ${page.route.replace(/\//g, ' ')} ${page.aliases?.join(' ') || ''}`);
		const purposeWords = tokens(page.purpose);
		const uiWords = new Set(tokens(page.uiReference.join(' ')));
		const score = titleWords.filter((term) => terms.has(term)).length * 5
			+ purposeWords.filter((term) => terms.has(term)).length
			+ Math.min(8, [...terms].filter((term) => uiWords.has(term)).length)
			+ (question.toLowerCase().includes(page.title.toLowerCase()) ? 20 : 0)
			+ (previous.includes(page.title.toLowerCase()) ? 2 : 0);
		return { page, score };
	}).filter((item) => item.score > 0).sort((a, b) => b.score - a.score).slice(0, 5);
	const guide = (page: typeof APPLICATION_PAGES[number]) => {
		if (page.reviewed) {
			const { title, purpose, controls, workflow, limitations } = page.reviewed;
			return { title, purpose, controls, workflow, limitations };
		}
		// Raw source files and route patterns are retained in the catalog, not sent to the model.
		let length = 0;
		const uiReference = page.uiReference.map((text, index) => ({ text, index, relevance: tokens(text).filter((term) => terms.has(term)).length }))
			.sort((a, b) => b.relevance - a.relevance || a.index - b.index)
			.map(({ text }) => text)
			.filter((text) => {
				if (length + text.length > 5000) return false;
				length += text.length;
				return true;
			});
		return { title: page.title, purpose: page.purpose, uiReference, referenceType: 'Static UI labels and help text; conditional, success, and error text are possible states, not current state. Nested controls may depend on mode, plan, or selection. Do not infer undocumented behavior or legal clearance from labels or legacy tips.' };
	};
	return {
		researchAccessGuidance: RESEARCH_ACCESS_GUIDANCE,
		currentPageKnowledge: current ? guide(current) : null,
		applicationPageGuides: ranked.map(({ page }) => guide(page)),
		applicationDirectory: APPLICATION_PAGES.filter((page) => page.navigable).map(({ title, purpose }) => ({ title, purpose })),
		availableNavigation: APPLICATION_PAGES.filter((page) => page.navigable && page !== current).map(({ title, route: destination }) => ({ title, route: destination }))
	};
}
